import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { TrustBand } from "@/components/site/TrustBand";
import { EditorialHero } from "@/components/site/wow/EditorialHero";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { breadcrumbs, aeoGeo } from "@/lib/seo/schema";
import { PRICE_GUIDES } from "@/lib/seo/priceGuides";

// Externa, kanoniska prissvar som redan finns på flata URL:er.
const EXTERNAL_PRICE_PAGES = [
  { path: "/vad-kostar-en-foretagsfilm", title: "Vad kostar en företagsfilm?", desc: "Prisspann 25–250 kkr beroende på typ av företagsfilm." },
  { path: "/vad-kostar-en-reklamfilm", title: "Vad kostar en reklamfilm?", desc: "Prisspann för reklamfilm i Sverige 2026." },
  { path: "/film-pris-kalkyl", title: "Räkna ut filmpris", desc: "Interaktiv kalkyl som uppskattar pris utifrån ert upplägg." },
];

export const Route = createFileRoute("/pris")({
  head: () => ({
    meta: [
      { title: "Vad kostar film? Prisguider 2026 | Fenny" },
      {
        name: "description",
        content:
          "Samlade prisguider för film: filmproduktion, videoproduktion, employer branding, abonnemang, produktfilm och animation. Sakliga prisspann och kostnadsdrivare.",
      },
      { property: "og:title", content: "Prisguider för film | Fenny" },
      {
        property: "og:description",
        content:
          "Vad kostar film? Sakliga prisspann för varje filmtyp och när abonnemang sänker styckpriset.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/pris" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/pris" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/pris", name: "Vad kostar film? Prisguider 2026 | Fenny", description: "Samlade prisguider för film: filmproduktion, videoproduktion, employer branding, abonnemang, produktfilm och animation. Sakliga prisspann och kostnadsdrivare."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: PRICE_GUIDES.map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `/pris/${g.slug}`,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Priser", path: "/pris" },
          ]),
        ),
      },
    ],
  }),
  component: PrisHubPage,
});

function PrisHubPage() {
  return (
    <div>
      <EditorialHero
        eyebrow="Prisguider"
        title="Vad kostar film?"
        highlight={[2, 3]}
        tone="mint"
        lede="Sakliga prisspann för varje filmtyp — vad som driver kostnaden och när ett abonnemang ger lägre pris per film än enskilda offerter."
      />

      <Section className="bg-surface/40">
        <EyebrowHeading eyebrow="Prisguider" title="Välj filmtyp" align="center" />
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {PRICE_GUIDES.map((g) => (
            <Link
              key={g.slug}
              to={`/pris/${g.slug}` as "/pris"}
              className="group flex flex-col rounded-3xl border border-white/10 bg-surface p-7 transition-colors hover:border-mint/30"
            >

              <h2 className="text-xl font-semibold leading-tight">{g.title}</h2>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground/80">
                {g.shortAnswer}
              </p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm text-foreground/80 group-hover:text-foreground">
                Se prisspann
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-20">
          <EyebrowHeading
            eyebrow="Fler prissvar"
            title="Specifika filmtyper"
            align="center"
          />
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {EXTERNAL_PRICE_PAGES.map((p) => (
            <Link
              key={p.path}
              to={p.path}
              className="group flex flex-col rounded-3xl border border-white/10 bg-surface p-7 transition-colors hover:border-mint/30"
            >
              <h3 className="text-lg font-semibold leading-tight">{p.title}</h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground/80">
                {p.desc}
              </p>
              <div className="mt-5 inline-flex items-center gap-2 text-sm text-foreground/80 group-hover:text-foreground">
                Läs mer
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <SignatureCta
        eyebrow="Vill ni ha ett konkret pris?"
        title="Vi räknar på ert behov."
        body="30 minuter, kostnadsfritt. Vi sätter mål och format och ger ett prisspann samma arbetsdag — Pulse, Prime eller en hybrid."
        tone="mint"
      />

      <TrustBand ctaLocation="pris_hub_trust_band" />
    </div>
  );
}
